<template>
  <div class="config-balanca">
    <div class="config-balanca__header">
      <div>
        <span class="config-balanca__eyebrow">PERIFÉRICOS DO TERMINAL</span>
        <h2><i class="fas fa-weight mr-2"></i>Balança de checkout</h2>
        <p>
          Configure a balança conectada diretamente a este PDV Desktop.
        </p>
      </div>
      <span
        :class="[
          'config-balanca__status',
          `config-balanca__status--${status.tipo}`,
        ]"
      >
        <i :class="status.icone"></i>{{ status.texto }}
      </span>
    </div>

    <div
      v-if="mensagem"
      :class="[
        'config-balanca__alert',
        erro ? 'config-balanca__alert--erro' : 'config-balanca__alert--sucesso',
      ]"
    >
      <i :class="erro ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'"></i>
      {{ mensagem }}
    </div>

    <div class="config-balanca__card">
      <div class="config-balanca__switch-row">
        <div>
          <strong>Usar balança neste terminal</strong>
          <small>
            Produtos marcados como “pesado” aguardarão o peso estabilizar.
          </small>
        </div>
        <label class="config-balanca__switch">
          <input v-model="form.habilitada" type="checkbox" />
          <span></span>
        </label>
      </div>

      <fieldset :disabled="ocupado || !form.habilitada">
        <div class="config-balanca__section-heading">
          <span class="config-balanca__section-icon">
            <i class="fas fa-plug"></i>
          </span>
          <div>
            <strong>Conexão serial</strong>
            <small>USB ou RS-232 conectado diretamente ao computador.</small>
          </div>
        </div>

        <div class="config-balanca__grid">
          <label>
            Modelo da balança
            <select v-model="form.modelo" @change="aplicarPadraoModelo">
              <option
                v-for="modelo in modelos"
                :key="modelo.codigo"
                :value="modelo.codigo"
              >
                {{ modelo.nome }}
              </option>
            </select>
          </label>

          <label>
            Porta serial
            <div class="config-balanca__input-action">
              <select v-model="form.portaSerial">
                <option value="">Selecione a porta</option>
                <option
                  v-if="portaSalvaNaoListada"
                  :value="form.portaSerial"
                >
                  {{ form.portaSerial }}
                </option>
                <option
                  v-for="porta in portas"
                  :key="porta.path"
                  :value="porta.path"
                >
                  {{ porta.nome }}
                </option>
              </select>
              <button
                type="button"
                title="Atualizar portas"
                @click="carregarPortas"
              >
                <i class="fas fa-sync-alt"></i>
              </button>
            </div>
          </label>

          <label>
            Velocidade
            <select v-model.number="form.baudRate">
              <option :value="9600">9600 baud (recomendado)</option>
              <option :value="4800">4800 baud</option>
              <option :value="2400">2400 baud</option>
            </select>
          </label>

          <label>
            Resolução do peso
            <select v-model.number="form.casasDecimais">
              <option :value="3">0,001 kg (3 casas)</option>
              <option :value="2">0,01 kg (2 casas)</option>
              <option :value="1">0,1 kg (1 casa)</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          class="config-balanca__detect-btn"
          :disabled="ocupado"
          @click="detectar"
        >
          <span class="config-balanca__detect-icon">
            <i :class="detectando ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
          </span>
          <span>
            <strong>{{ detectando ? "Detectando balança..." : "Detectar automaticamente" }}</strong>
            <small>Testa as portas e velocidades disponíveis neste computador.</small>
          </span>
          <i class="fas fa-chevron-right"></i>
        </button>

        <details class="config-balanca__advanced">
          <summary>Configuração avançada de estabilização</summary>
          <div class="config-balanca__advanced-grid">
            <label>
              Tolerância
              <div class="config-balanca__number-suffix">
                <input v-model.number="form.toleranciaG" type="number" min="0" max="1000" />
                <span>g</span>
              </div>
            </label>
            <label>
              Amostras estáveis
              <input v-model.number="form.amostrasJanela" type="number" min="2" max="50" />
            </label>
            <label>
              Peso mínimo
              <div class="config-balanca__number-suffix">
                <input v-model.number="form.pesoMinimoG" type="number" min="0" max="10000" />
                <span>g</span>
              </div>
            </label>
            <label>
              Intervalo de leitura
              <div class="config-balanca__number-suffix">
                <input v-model.number="form.intervaloMs" type="number" min="100" max="5000" />
                <span>ms</span>
              </div>
            </label>
          </div>
        </details>
      </fieldset>

      <div class="config-balanca__diagnostico">
        <div>
          <span>Conexão</span>
          <strong :class="{ 'config-balanca__ok': diagnostico.conectado }">
            {{ diagnostico.conectado ? "Conectada" : "Desconectada" }}
          </strong>
        </div>
        <div>
          <span>Porta em uso</span>
          <strong>{{ diagnostico.porta || "—" }}</strong>
        </div>
        <div>
          <span>Velocidade</span>
          <strong>{{ diagnostico.baudRate ? `${diagnostico.baudRate} baud` : "—" }}</strong>
        </div>
        <div>
          <span>Último peso</span>
          <strong>{{ pesoFormatado }}</strong>
        </div>
      </div>

      <div class="config-balanca__actions">
        <button
          class="config-balanca__btn config-balanca__btn--outline"
          :disabled="ocupado || !form.habilitada || !form.portaSerial"
          @click="testar"
        >
          <i :class="testando ? 'fas fa-spinner fa-spin mr-1' : 'fas fa-vial mr-1'"></i>
          {{ testando ? "Lendo peso..." : "Testar leitura" }}
        </button>
        <button
          class="config-balanca__btn config-balanca__btn--primary"
          :disabled="ocupado"
          @click="salvar"
        >
          <i :class="salvando ? 'fas fa-spinner fa-spin mr-1' : 'fas fa-save mr-1'"></i>
          {{ salvando ? "Salvando..." : "Salvar configuração" }}
        </button>
      </div>
    </div>

    <div class="config-balanca__info">
      <i class="fas fa-info-circle"></i>
      <span>
        Para a Toledo Prix 3 Fit, configure <strong>C-13 = SERIAL</strong>,
        <strong>C-14 = Prt5</strong> e <strong>C-15 = 9600</strong>.
        O PDV Desktop faz toda a leitura, sem outro aplicativo aberto.
      </span>
    </div>
  </div>
</template>

<script>
import BalancaCheckoutService, {
  DEFAULT_CONFIG,
} from "@/modules/PdvDesktop/services/BalancaCheckoutService";

export default {
  name: "ConfiguracaoBalanca",
  data() {
    return {
      form: { ...DEFAULT_CONFIG },
      modelos: [],
      portas: [],
      diagnostico: {},
      carregando: true,
      salvando: false,
      detectando: false,
      testando: false,
      mensagem: "",
      erro: false,
      removerListener: null,
    };
  },
  computed: {
    ocupado() {
      return this.carregando || this.salvando || this.detectando || this.testando;
    },
    portaSalvaNaoListada() {
      return Boolean(
        this.form.portaSerial &&
          !this.portas.some((porta) => porta.path === this.form.portaSerial)
      );
    },
    status() {
      if (!this.form.habilitada) {
        return {
          tipo: "inativo",
          icone: "fas fa-circle",
          texto: "Desativada",
        };
      }
      if (this.diagnostico.conectado) {
        return {
          tipo: "ok",
          icone: "fas fa-check-circle",
          texto: "Balança pronta",
        };
      }
      return {
        tipo: "alerta",
        icone: "fas fa-exclamation-circle",
        texto: "Não conectada",
      };
    },
    pesoFormatado() {
      const peso = this.diagnostico?.ultimaAmostra?.peso;
      if (!Number.isFinite(Number(peso))) return "—";
      return `${Number(peso).toLocaleString("pt-BR", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} kg`;
    },
  },
  async mounted() {
    this.removerListener = BalancaCheckoutService.onEvento((payload) => {
      if (payload.tipo === "conexao") {
        this.$set(this.diagnostico, "conectado", Boolean(payload.conectado));
        if (payload.porta) this.$set(this.diagnostico, "porta", payload.porta);
      }
      if (payload.tipo === "peso_tempo_real" || payload.tipo === "peso_estavel") {
        this.$set(this.diagnostico, "ultimaAmostra", payload);
      }
      if (payload.tipo === "erro") {
        this.$set(this.diagnostico, "ultimoErro", payload.mensagem);
      }
    });

    try {
      const [form, modelos] = await Promise.all([
        BalancaCheckoutService.carregarConfiguracao(),
        BalancaCheckoutService.listarModelos(),
      ]);
      this.form = form;
      this.modelos = modelos;
      await this.carregarPortas(false);
      if (this.form.habilitada && this.form.portaSerial) {
        try {
          this.diagnostico = await BalancaCheckoutService.conectar(this.form);
        } catch (error) {
          this.diagnostico = await BalancaCheckoutService.diagnostico();
          this.erro = true;
          this.mensagem = error?.message || "Não foi possível abrir a porta serial.";
        }
      } else {
        this.diagnostico = await BalancaCheckoutService.diagnostico(this.form);
      }
    } catch (error) {
      this.erro = true;
      this.mensagem = error?.message || "Não foi possível carregar a configuração.";
    } finally {
      this.carregando = false;
    }
  },
  beforeDestroy() {
    if (typeof this.removerListener === "function") this.removerListener();
  },
  methods: {
    async carregarPortas(mostrarMensagem = true) {
      try {
        this.portas = await BalancaCheckoutService.listarPortas();
        if (mostrarMensagem) {
          this.erro = false;
          this.mensagem = this.portas.length
            ? `${this.portas.length} porta(s) serial(is) encontrada(s).`
            : "Nenhuma porta serial encontrada.";
        }
      } catch (error) {
        this.portas = [];
        if (mostrarMensagem) {
          this.erro = true;
          this.mensagem = error?.message || "Não foi possível listar as portas.";
        }
      }
    },
    aplicarPadraoModelo() {
      const modelo = this.modelos.find((item) => item.codigo === this.form.modelo);
      if (!modelo) return;
      this.form.baudRate = Number(modelo.baudDefault || 9600);
      this.form.casasDecimais = Number(modelo.casasDecimaisDefault || 3);
    },
    async detectar() {
      this.detectando = true;
      this.mensagem = "";
      try {
        this.form.habilitada = true;
        const resultado = await BalancaCheckoutService.detectar(this.form);
        this.form = {
          ...this.form,
          ...resultado.config,
          portaSerial: resultado.porta.path,
          baudRate: resultado.baudRate,
          habilitada: true,
        };
        this.form = await BalancaCheckoutService.salvarConfiguracao(this.form);
        await this.carregarPortas(false);
        this.diagnostico = await BalancaCheckoutService.diagnostico();
        this.erro = false;
        this.mensagem = `Balança detectada em ${resultado.porta.path} (${resultado.baudRate} baud).`;
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "A balança não foi detectada.";
        this.diagnostico = await BalancaCheckoutService.diagnostico();
      } finally {
        this.detectando = false;
      }
    },
    async testar() {
      this.testando = true;
      this.mensagem = "";
      try {
        await BalancaCheckoutService.conectar(this.form);
        const amostra = await BalancaCheckoutService.lerPeso(this.form);
        this.$set(this.diagnostico, "ultimaAmostra", amostra);
        this.diagnostico = await BalancaCheckoutService.diagnostico();
        if (amostra.status !== "estavel") {
          throw new Error(`A balança respondeu com peso ${amostra.status}.`);
        }
        this.erro = false;
        this.mensagem = `Leitura recebida: ${Number(amostra.peso).toLocaleString(
          "pt-BR",
          { minimumFractionDigits: 3, maximumFractionDigits: 3 }
        )} kg.`;
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "Não foi possível ler o peso.";
        this.diagnostico = await BalancaCheckoutService.diagnostico();
      } finally {
        this.testando = false;
      }
    },
    async salvar() {
      this.salvando = true;
      this.mensagem = "";
      try {
        if (this.form.habilitada && !this.form.portaSerial) {
          throw new Error("Selecione uma porta ou use a detecção automática.");
        }
        this.form = await BalancaCheckoutService.salvarConfiguracao(this.form);
        if (this.form.habilitada) {
          this.diagnostico = await BalancaCheckoutService.conectar(this.form);
        } else {
          await BalancaCheckoutService.desconectar();
          this.diagnostico = await BalancaCheckoutService.diagnostico(this.form);
        }
        this.erro = false;
        this.mensagem = "Configuração da balança salva neste PDV Desktop.";
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "Não foi possível salvar a configuração.";
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.config-balanca {
  padding: 28px;
  max-width: 980px;
  margin: 0 auto;
  color: #263238;
}
.config-balanca__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 22px;
}
.config-balanca__eyebrow {
  display: block;
  margin-bottom: 6px;
  color: var(--primary-color, #ee7361);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.09em;
}
.config-balanca__header h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}
.config-balanca__header p {
  margin: 0;
  color: #64748b;
}
.config-balanca__status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
}
.config-balanca__status--ok {
  background: #dcfce7;
  color: #166534;
}
.config-balanca__status--alerta {
  background: #fef3c7;
  color: #92400e;
}
.config-balanca__status--inativo {
  background: #e2e8f0;
  color: #475569;
}
.config-balanca__card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 5px 18px rgba(15, 23, 42, 0.06);
}
.config-balanca__switch-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.config-balanca__switch-row small,
.config-balanca__section-heading small,
.config-balanca__detect-btn small {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-weight: 400;
}
.config-balanca__switch input {
  display: none;
}
.config-balanca__switch span {
  display: block;
  width: 46px;
  height: 25px;
  border-radius: 20px;
  background: #cbd5e1;
  position: relative;
  cursor: pointer;
  transition: 0.2s;
}
.config-balanca__switch span::after {
  content: "";
  position: absolute;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background: #fff;
  top: 3px;
  left: 3px;
  transition: 0.2s;
}
.config-balanca__switch input:checked + span {
  background: var(--primary-color, #ee7361);
}
.config-balanca__switch input:checked + span::after {
  transform: translateX(21px);
}
.config-balanca fieldset {
  margin: 0;
  padding: 22px 24px;
  border: 0;
}
.config-balanca fieldset:disabled {
  opacity: 0.56;
}
.config-balanca__section-heading {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 20px;
}
.config-balanca__section-icon,
.config-balanca__detect-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  color: var(--primary-color, #ee7361);
  background: #fde8e5;
}
.config-balanca__grid,
.config-balanca__advanced-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.config-balanca__grid label,
.config-balanca__advanced-grid label {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}
.config-balanca__grid select,
.config-balanca__advanced-grid input {
  display: block;
  width: 100%;
  min-height: 42px;
  margin-top: 7px;
  padding: 9px 11px;
  color: #334155;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
}
.config-balanca__grid select:focus,
.config-balanca__advanced-grid input:focus {
  border-color: var(--primary-color, #ee7361);
  box-shadow: 0 0 0 3px rgba(238, 115, 97, 0.12);
}
.config-balanca__input-action {
  display: flex;
  gap: 7px;
  margin-top: 7px;
}
.config-balanca__input-action select {
  min-width: 0;
  margin-top: 0;
}
.config-balanca__input-action button {
  flex: 0 0 42px;
  color: #64748b;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
}
.config-balanca__detect-btn {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  margin-top: 20px;
  padding: 12px;
  text-align: left;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.config-balanca__detect-btn > span:nth-child(2) {
  flex: 1;
}
.config-balanca__detect-btn > .fa-chevron-right {
  color: #94a3b8;
  font-size: 12px;
}
.config-balanca__detect-btn:hover:not(:disabled) {
  background: #fff;
  border-color: var(--primary-color, #ee7361);
  box-shadow: 0 4px 12px rgba(238, 115, 97, 0.1);
}
.config-balanca__advanced {
  margin-top: 18px;
  color: #64748b;
  font-size: 13px;
}
.config-balanca__advanced summary {
  cursor: pointer;
  font-weight: 600;
}
.config-balanca__advanced-grid {
  margin-top: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 9px;
}
.config-balanca__number-suffix {
  position: relative;
}
.config-balanca__number-suffix span {
  position: absolute;
  right: 11px;
  bottom: 11px;
  color: #94a3b8;
  font-weight: 600;
}
.config-balanca__diagnostico {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  padding: 1px 24px 22px;
}
.config-balanca__diagnostico div {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
}
.config-balanca__diagnostico div:first-child {
  border-radius: 9px 0 0 9px;
}
.config-balanca__diagnostico div:last-child {
  border-right: 0;
  border-radius: 0 9px 9px 0;
}
.config-balanca__diagnostico span {
  color: #64748b;
  font-size: 11px;
}
.config-balanca__diagnostico strong {
  overflow: hidden;
  margin-top: 3px;
  color: #334155;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.config-balanca__diagnostico .config-balanca__ok {
  color: #15803d;
}
.config-balanca__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}
.config-balanca__btn {
  min-height: 42px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-weight: 600;
}
.config-balanca__btn:disabled,
.config-balanca__detect-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.config-balanca__btn--primary {
  color: #fff;
  background: var(--primary-color, #ee7361);
  box-shadow: 0 3px 9px rgba(238, 115, 97, 0.22);
}
.config-balanca__btn--outline {
  color: #475569;
  background: #fff;
  border-color: #cbd5e1;
}
.config-balanca__alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
}
.config-balanca__alert--sucesso {
  color: #166534;
  background: #dcfce7;
}
.config-balanca__alert--erro {
  color: #991b1b;
  background: #fee2e2;
}
.config-balanca__info {
  display: flex;
  gap: 9px;
  margin-top: 16px;
  padding: 13px 15px;
  color: #64748b;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 9px;
  font-size: 13px;
}
.config-balanca__info > i {
  margin-top: 2px;
  color: #ea580c;
}
@media (max-width: 760px) {
  .config-balanca__grid,
  .config-balanca__advanced-grid,
  .config-balanca__diagnostico {
    grid-template-columns: 1fr;
  }
  .config-balanca__header {
    flex-direction: column;
  }
  .config-balanca__diagnostico div {
    border-right: 0;
    border-bottom: 1px solid #e2e8f0;
    border-radius: 0;
  }
  .config-balanca__actions {
    flex-direction: column-reverse;
  }
}
</style>
